import React, { useState } from 'react';
import { Sidebar } from '../../components/shared/Sidebar';
import { DashboardHeader } from '../../components/shared/DashboardHeader';
import { StatCard } from '../../components/shared/StatCard';
import { PropertyCard } from '../../components/landlord/PropertyCard';
import { QuickActions } from '../../components/shared/QuickActions';
import { Breadcrumbs } from '../../components/shared/Breadcrumbs';
import { PropertyManagementModal } from '../../components/modals/PropertyManagementModal';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { MOCK_PROPERTIES, MOCK_LANDLORD } from '../../utils/mockData';
import { Building, Users, Wallet, TrendingUp, Plus, FileSignature, PieChart, FolderKanban, Sparkles } from 'lucide-react';
import { formatCurrency } from '../../utils/calculations';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
export function LandlordDashboard() {
  const {
    user
  } = useAuth();
  const {
    toast
  } = useToast();
  const navigate = useNavigate();
  const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
  const properties = MOCK_PROPERTIES;
  const handlePropertySuccess = () => {
    toast.success('Property added successfully!');
    setIsPropertyModalOpen(false);
  };
  return <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900 pb-16 lg:pb-0 transition-colors" data-id="element-3595">
      <Sidebar data-id="element-3596" />

      <main className="flex-1 lg:ml-[240px] transition-all duration-300" data-id="element-3597">
        <DashboardHeader data-id="element-3598" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-id="element-3599">
          <Breadcrumbs data-id="element-3600" />

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-3601">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-3602">
              <div data-id="element-3603">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3604">
                  Dashboard
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-3605">
                  Overview of your properties and settlements.
                </p>
              </div>
              <button onClick={() => setIsPropertyModalOpen(true)} className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors" data-id="element-3606">
                <Plus className="h-4 w-4 mr-2" data-id="element-3607" />
                Add Property
              </button>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-3608">
              <StatCard title="Total Properties" value={properties.length} icon={Building} color="blue" data-id="element-3609" />
              <StatCard title="Active Tenants" value={MOCK_LANDLORD.totalTenants} icon={Users} color="green" data-id="element-3610" />
              <StatCard title="Total Revenue" value={formatCurrency(MOCK_LANDLORD.totalRevenue)} icon={Wallet} color="purple" trend={{
              value: 12,
              isPositive: true
            }} data-id="element-3611" />
              <StatCard title="Occupancy Rate" value="92%" icon={TrendingUp} color="orange" data-id="element-3612" />
            </div>

            {/* New Features Highlight */}
            <div className="mb-8" data-id="element-3613">
              <div className="flex items-center mb-4" data-id="element-3614">
                <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" data-id="element-3615" />
                <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-3616">
                  New Features
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3617">
                <motion.button onClick={() => navigate('/landlord/portfolio')} whileHover={{
                y: -2
              }} className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white text-left shadow-lg hover:shadow-xl transition-all" data-id="element-3618">
                  <div className="flex items-start justify-between mb-4" data-id="element-3619">
                    <div className="h-12 w-12 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm" data-id="element-3620">
                      <PieChart className="h-6 w-6" data-id="element-3621" />
                    </div>
                    <span className="text-xs bg-white/20 px-2 py-1 rounded-full backdrop-blur-sm" data-id="element-3622">
                      New
                    </span>
                  </div>
                  <h3 className="text-lg font-bold mb-1" data-id="element-3623">
                    Portfolio Analytics
                  </h3>
                  <p className="text-sm text-blue-100" data-id="element-3624">
                    Track revenue, occupancy & tenant retention
                  </p>
                </motion.button>

                <motion.button onClick={() => navigate('/landlord/lease-generator')} whileHover={{
                y: -2
              }} className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white text-left shadow-lg hover:shadow-xl transition-all" data-id="element-3625">
                  <div className="flex items-start justify-between mb-4" data-id="element-3626">
                    <div className="h-12 w-12 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm" data-id="element-3627">
                      <FileSignature className="h-6 w-6" data-id="element-3628" />
                    </div>
                    <span className="text-xs bg-white/20 px-2 py-1 rounded-full backdrop-blur-sm" data-id="element-3629">
                      New
                    </span>
                  </div>
                  <h3 className="text-lg font-bold mb-1" data-id="element-3630">AI Lease Generator</h3>
                  <p className="text-sm text-green-100" data-id="element-3631">
                    Create legally sound leases in minutes
                  </p>
                </motion.button>

                <motion.button onClick={() => navigate('/landlord/applications')} whileHover={{
                y: -2
              }} className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white text-left shadow-lg hover:shadow-xl transition-all" data-id="element-3632">
                  <div className="flex items-start justify-between mb-4" data-id="element-3633">
                    <div className="h-12 w-12 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm" data-id="element-3634">
                      <FolderKanban className="h-6 w-6" data-id="element-3635" />
                    </div>
                    <span className="text-xs bg-white/20 px-2 py-1 rounded-full backdrop-blur-sm" data-id="element-3636">
                      New
                    </span>
                  </div>
                  <h3 className="text-lg font-bold mb-1" data-id="element-3637">
                    Application Builder
                  </h3>
                  <p className="text-sm text-purple-100" data-id="element-3638">
                    Create custom rental application forms
                  </p>
                </motion.button>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mb-8" data-id="element-3639">
              <QuickActions data-id="element-3640" />
            </div>

            {/* Properties Grid */}
            <section className="mb-8" data-id="element-3641">
              <div className="flex justify-between items-center mb-4" data-id="element-3642">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-3643">
                  Your Properties
                </h2>
                <button onClick={() => navigate('/landlord/properties')} className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium" data-id="element-3644">
                  View All
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3645">
                {properties.map((property, idx) => <motion.div key={property.id} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.3,
                delay: idx * 0.1
              }} data-id="element-3646">
                    <PropertyCard property={property} data-id="element-3647" />
                  </motion.div>)}
                {/* Add Property Card */}
                <motion.button onClick={() => setIsPropertyModalOpen(true)} whileHover={{
                y: -4
              }} className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-6 flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 hover:border-blue-500 dark:hover:border-blue-400 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all cursor-pointer h-full min-h-[200px]" data-id="element-3648">
                  <Plus className="h-8 w-8 mb-2" data-id="element-3649" />
                  <span className="text-sm font-medium" data-id="element-3650">Add New Property</span>
                </motion.button>
              </div>
            </section>

            {/* Recent Settlements */}
            <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors" data-id="element-3651">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700" data-id="element-3652">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-3653">
                  Recent Settlements
                </h2>
              </div>
              <div className="overflow-x-auto" data-id="element-3654">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3655">
                  <thead className="bg-gray-50 dark:bg-gray-700/50" data-id="element-3656">
                    <tr data-id="element-3657">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3658">
                        Tenant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3659">
                        Property
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3660">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3661">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3662">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3663">
                    {[{
                    tenant: 'Chioma Adebayo',
                    property: 'Sunrise Apartments, 4B',
                    amount: 150000,
                    date: 'Oct 1, 2023'
                  }, {
                    tenant: 'Emmanuel Okon',
                    property: 'Lekki Gardens, 2A',
                    amount: 180000,
                    date: 'Oct 1, 2023'
                  }, {
                    tenant: 'Sarah Johnson',
                    property: 'Victoria Island Tower, 12C',
                    amount: 320000,
                    date: 'Oct 1, 2023'
                  }, {
                    tenant: 'Michael Chen',
                    property: 'Ikoyi Heights, 5B',
                    amount: 250000,
                    date: 'Oct 1, 2023'
                  }].map((settlement, i) => <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors" data-id="element-3664">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-3665">
                          {settlement.tenant}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-3666">
                          {settlement.property}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white" data-id="element-3667">
                          {formatCurrency(settlement.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-3668">
                          {settlement.date}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-3669">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" data-id="element-3670">
                            Settled
                          </span>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </section>
          </motion.div>
        </div>
      </main>

      {/* Property Management Modal */}
      <PropertyManagementModal isOpen={isPropertyModalOpen} onClose={() => setIsPropertyModalOpen(false)} mode="add" onSuccess={handlePropertySuccess} data-id="element-3671" />
    </div>;
}